/**
 * Hook para obtener balance de usuario NBC TALK PAY
 * Integra con el servicio real de balance del backend
 */
"use client";

import { useState, useEffect } from 'react';
import { NBCUser, UserBalance } from '@/types/nbc-types';
import { calculateEurFromUsdt } from '@/lib/services/exchange-rate';

export interface NBCBalance {
  usdt: string;
  eur: string;
  raw_balance: number;
  blockchain_balance?: number;
  pending_adjustments?: number;
}

interface UseNBCBalanceResult {
  balance: NBCBalance | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

// Función para obtener balance via proxy interno
const fetchBalanceFromNBC = async (walletAddress: string): Promise<NBCBalance> => {
  try {
    console.log(`💰 [useNBCBalance] Fetching balance for wallet: ${walletAddress}`);
    
    // Usar el proxy interno de CasinoPay (similar a get_wallet_by_user)
    const response = await fetch('/api/get_balance', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        walletAddress: walletAddress,
        tokenAmount: 1,
        blockchainNetwork: 'polygon_public',
        tokenContractAddress: process.env.NEXT_PUBLIC_USDT_CONTRACT || '0xc2132D05D31c914a87C6611C10748AEb04B58e8F',
      }),
    });

    console.log(`📡 [useNBCBalance] Response status: ${response.status}`);

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`❌ [useNBCBalance] HTTP Error: ${response.status} - ${errorText}`);
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    console.log(`📡 [useNBCBalance] Response data:`, data);
    
    if (data.success && data.data) {
      const usdtBalance = data.data.balance || 0;
      const eurBalance = await calculateEurFromUsdt(usdtBalance);

      const balanceResult: NBCBalance = {
        usdt: usdtBalance.toFixed(2),
        eur: eurBalance.toFixed(2),
        raw_balance: usdtBalance,
        blockchain_balance: data.data.blockchain_balance,
        pending_adjustments: data.data.pending_adjustments,
      };
      
      console.log(`✅ [useNBCBalance] Balance calculated:`, balanceResult);
      return balanceResult;
    } else {
      throw new Error(data.error || data.message || 'Error fetching balance');
    }
    
  } catch (error) {
    console.error('❌ [useNBCBalance] Error fetching NBC balance:', error);
    throw error;
  }
};

export function useNBCBalance(nbcUser?: NBCUser): UseNBCBalanceResult {
  const [balance, setBalance] = useState<NBCBalance | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchBalance = async () => {
    if (!nbcUser?.success || (!nbcUser.wallet_address && !nbcUser.public_key)) {
      setBalance(null);
      setError('Usuario no verificado o sin wallet');
      return;
    }

    const walletAddress = nbcUser.wallet_address || nbcUser.public_key || '';
    
    setIsLoading(true);
    setError(null);

    try {
      const balanceData = await fetchBalanceFromNBC(walletAddress);
      setBalance(balanceData);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error desconocido';
      setError(errorMessage);
      console.error('Balance fetch error:', err);
      
      // Fallback: balance cero en caso de error
      setBalance({
        usdt: '0.00',
        eur: '0.00',
        raw_balance: 0,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Auto-fetch cuando cambia el usuario
  useEffect(() => {
    fetchBalance();
  }, [nbcUser?.wallet_address, nbcUser?.public_key, nbcUser?.success]);

  return {
    balance,
    isLoading,
    error,
    refetch: fetchBalance,
  };
}